import React from 'react';
import styled from 'styled-components';
import { Text, TextBlock } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';

interface Column {
  key: string;
  label: string;
  width?: string;
}

interface DataTableProps {
  title?: string;
  columns: Column[];
  rows: Record<string, unknown>[];
  emptyMessage?: string;
}

const TableContainer = styled.div`
  display: grid;
  gap: 8px;
`;

const TableTitle = styled(TextBlock).attrs({
  styleType: 'mediumBold',
  color: colors.charcoal,
})``;

const TableHeader = styled.div<{ columnCount: number }>`
  display: grid;
  grid-template-columns: ${({ columnCount }) => `repeat(${columnCount}, 1fr)`};
  gap: 16px;
  background-color: ${colors.pearl};
  border-top: 1px solid ${colors.silt};
  box-shadow: 0 1px 0 ${colors.silt};
  padding: 8px;
  min-height: 33px;
  align-items: center;
`;

const HeaderCell = styled(Text).attrs({
  styleType: 'small',
  color: colors.smoke,
  uppercase: true,
})``;

const TableRow = styled.div<{ columnCount: number }>`
  display: grid;
  grid-template-columns: ${({ columnCount }) => `repeat(${columnCount}, 1fr)`};
  gap: 16px;
  padding: 8px;
  border-bottom: 1px solid ${colors.silt};
`;

const TableCell = styled(Text).attrs({
  styleType: 'medium',
  color: colors.charcoal,
})`
  display: block;
`;

const EmptyMessage = styled(Text).attrs({
  styleType: 'small',
  color: colors.cloud,
})`
  display: block;
  padding: 8px;
`;

const DataTable: React.FC<DataTableProps> = ({
  title,
  columns,
  rows,
  emptyMessage = 'No data available',
}) => (
  <TableContainer>
    {title && <TableTitle>{title}</TableTitle>}
    <TableHeader columnCount={columns.length}>
      {columns.map(column => (
        <HeaderCell key={column.key}>{column.label}</HeaderCell>
      ))}
    </TableHeader>
    {rows.length === 0 ? (
      <EmptyMessage>{emptyMessage}</EmptyMessage>
    ) : (
      rows.map((row, index) => (
        <TableRow key={index} columnCount={columns.length}>
          {columns.map(column => (
            <TableCell key={column.key}>{(row[column.key] as string | number) ?? '-'}</TableCell>
          ))}
        </TableRow>
      ))
    )}
  </TableContainer>
);

export default DataTable;
